// The Pro Studio redesign's nav, in the prototype's order and groups (spec
// ui-perf/pro-studio-redesign, "The shell"). An item links where its redesigned page exists and is
// `Soon` elsewhere, so building a page makes its item live with one `to` here.
import type { RegisteredRouter, RouteIds } from "@tanstack/react-router";
import {
  ChartLine,
  CircleUserRound,
  Film,
  Folder,
  History,
  House,
  Image,
  LayoutTemplate,
  Lightbulb,
  ListVideo,
  type LucideIcon,
  Megaphone,
  Music,
  Package,
  Scissors,
  Tag,
  Users,
} from "lucide-react";

/** Where an item goes: a redesigned page under the brand's `pro`, or none. */
export type StudioTarget =
  | {
      scope: "page";
      to:
        | "/teams/$teamId/brands/$brandId/pro"
        | "/teams/$teamId/brands/$brandId/pro/create-talking"
        | "/teams/$teamId/brands/$brandId/pro/create-image"
        | "/teams/$teamId/brands/$brandId/pro/create-video"
        | "/teams/$teamId/brands/$brandId/pro/create-sound"
        | "/teams/$teamId/brands/$brandId/pro/actors"
        | "/teams/$teamId/brands/$brandId/pro/generations"
        | "/teams/$teamId/brands/$brandId/pro/templates"
        | "/teams/$teamId/brands/$brandId/pro/library"
        | "/teams/$teamId/brands/$brandId/pro/products"
        | "/teams/$teamId/brands/$brandId/pro/brands"
        | "/teams/$teamId/brands/$brandId/pro/editor";
    }
  | { scope: "soon" };

export interface StudioNavItem {
  label: string;
  icon: LucideIcon;
  target: StudioTarget;
  /** The prototype's badge beside the label. */
  badge?: string;
}

const SOON = { scope: "soon" } as const;

export const STUDIO_NAV = [
  {
    title: undefined,
    items: [
      {
        label: "Home",
        icon: House,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro" },
      },
    ],
  },
  {
    title: "Create",
    items: [
      {
        label: "Talking head",
        icon: CircleUserRound,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/create-talking" },
      },
      {
        label: "Image",
        icon: Image,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/create-image" },
      },
      {
        label: "Video",
        icon: Film,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/create-video" },
      },
      // The multi-shot storyboard is not built (G16, #981).
      { label: "Storyboard ad", icon: ListVideo, target: SOON },
      {
        label: "Voice and sound",
        icon: Music,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/create-sound" },
      },
      // The editor's project list in Pro; a project opens the editor, full-window (spec ui-switch, e6).
      {
        label: "Video Editor",
        icon: Scissors,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/editor" },
      },
    ],
  },
  {
    // The prototype's divider, then Templates on its own.
    title: undefined,
    items: [
      {
        label: "Templates",
        icon: LayoutTemplate,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/templates" },
        badge: "New",
      },
    ],
  },
  {
    title: "Your work",
    items: [
      {
        label: "Generations",
        icon: History,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/generations" },
      },
      {
        label: "Library",
        icon: Folder,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/library" },
      },
    ],
  },
  {
    title: "Your brand",
    items: [
      {
        label: "Products",
        icon: Package,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/products" },
      },
      {
        label: "Actors",
        icon: Users,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/actors" },
      },
      {
        label: "Brands",
        icon: Tag,
        target: { scope: "page", to: "/teams/$teamId/brands/$brandId/pro/brands" },
      },
    ],
  },
  {
    // The ads area has no API yet (#253).
    title: "Launch and measure",
    items: [
      { label: "Ad Inspiration", icon: Lightbulb, target: SOON },
      { label: "Analytics", icon: ChartLine, target: SOON },
      { label: "Ads Publisher", icon: Megaphone, target: SOON },
    ],
  },
] as const satisfies readonly { title: string | undefined; items: readonly StudioNavItem[] }[];

type Item = (typeof STUDIO_NAV)[number]["items"][number];
/** A nav item a page can belong to: one that links somewhere in the redesign. */
export type StudioSection = Extract<Item, { target: { scope: "page" } }>["label"];

const B = "/signed-in/teams/$teamId/brands/$brandId/studio/pro";
const SIMPLE_PAGE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple";
type StudioPageId = Extract<
  RouteIds<RegisteredRouter["routeTree"]>,
  `${typeof B}/${string}` | typeof SIMPLE_PAGE | `${typeof SIMPLE_PAGE}/${string}`
>;

/** A redesigned page outside the nav, in the Pro frame: Settings, opened from the header's gear. */
export const SETTINGS_PLACE = "Settings";
/** Where a page sits: its nav item, Settings, or `null` for Simple (its nav is `simple/`'s). */
export type StudioPlace = StudioSection | typeof SETTINGS_PLACE | null;

/**
 * Every redesigned page and its place: its nav item, Settings, or `null` for Simple. A new `pro`
 * route fails tsc until it has an entry here.
 */
const PAGES: Record<StudioPageId, StudioPlace> = {
  [`${B}/`]: "Home",
  [SIMPLE_PAGE]: null,
  // Simple's pages: Home, the guided page (spec ui-perf/simple-home) and Home's two composers
  // (spec ui-perf/simple-flows).
  [`${SIMPLE_PAGE}/`]: null,
  [`${SIMPLE_PAGE}/make`]: null,
  [`${SIMPLE_PAGE}/ugc-video-ad`]: null,
  [`${SIMPLE_PAGE}/image-ad`]: null,
  // Simple's sections (specs ui-perf/simple-videos and after).
  [`${SIMPLE_PAGE}/videos`]: null,
  [`${SIMPLE_PAGE}/videos/$tool`]: null,
  [`${SIMPLE_PAGE}/images`]: null,
  [`${SIMPLE_PAGE}/actor`]: null,
  [`${SIMPLE_PAGE}/fix-image`]: null,
  [`${SIMPLE_PAGE}/library`]: null,
  [`${SIMPLE_PAGE}/ads`]: null,
  [`${SIMPLE_PAGE}/audio/$sound`]: null,
  [`${SIMPLE_PAGE}/brand`]: null,
  [`${SIMPLE_PAGE}/brand/edit`]: null,
  [`${SIMPLE_PAGE}/products/new`]: null,
  [`${SIMPLE_PAGE}/products/$productId`]: null,
  [`${B}/create-talking`]: "Talking head",
  [`${B}/create-image`]: "Image",
  [`${B}/create-video`]: "Video",
  [`${B}/create-sound`]: "Voice and sound",
  [`${B}/actors`]: "Actors",
  [`${B}/generations`]: "Generations",
  [`${B}/templates`]: "Templates",
  [`${B}/library`]: "Library",
  // The Library's own files, composed in `app/pro-library.tsx` (spec ui-switch, e3).
  [`${B}/library/files`]: "Library",
  [`${B}/products`]: "Products",
  // The brands area's own pages, composed in `app/pro-brands.tsx` (spec ui-switch, e4).
  [`${B}/products/new`]: "Products",
  [`${B}/products/$productId`]: "Products",
  [`${B}/brands`]: "Brands",
  [`${B}/brands/new`]: "Brands",
  [`${B}/brand`]: "Brands",
  [`${B}/brand/edit`]: "Brands",
  [`${B}/settings`]: SETTINGS_PLACE,
  // Other areas' pages as Settings' tabs, composed in `app/pro-settings.tsx` (spec ui-switch, e5a).
  [`${B}/settings/account`]: SETTINGS_PLACE,
  [`${B}/settings/appearance`]: SETTINGS_PLACE,
  [`${B}/settings/achievements`]: SETTINGS_PLACE,
  [`${B}/settings/tokens`]: SETTINGS_PLACE,
  // Composed in `app/` (spec ui-switch, e5b): the Sounds page's library, and New team, which,
  // opened from the account menu, belongs to no nav item, as Settings.
  [`${B}/sounds`]: "Voice and sound",
  [`${B}/teams/new`]: SETTINGS_PLACE,
  // The editor's project list, composed in `app/pro-editor.tsx` (spec ui-switch, e6).
  [`${B}/editor`]: "Video Editor",
  // A `pro/<anything>` with no page: in Pro's frame, on no nav item (spec
  // ui-perf/pro-fixes-notfound-case).
  [`${B}/$`]: SETTINGS_PLACE,
};

/** The nav item of the deepest matched route, or `undefined` outside the redesign. */
export function studioSectionOf(routeId: string): StudioPlace | undefined {
  return (PAGES as Record<string, StudioPlace | undefined>)[routeId];
}

export const PRO_HOME = "/teams/$teamId/brands/$brandId/pro";
export const SIMPLE = "/teams/$teamId/brands/$brandId/simple";
export const SETTINGS = "/teams/$teamId/brands/$brandId/pro/settings";
export type StudioPath =
  | Extract<StudioTarget, { scope: "page" }>["to"]
  | typeof SIMPLE
  | typeof SETTINGS;

/**
 * Where a brand switch goes: the same section's page under the new brand (a product or a job of
 * the old brand means nothing under the new one), Simple from Simple, Settings from Settings, else
 * Pro Home.
 */
export function sectionPath(section: StudioPlace | undefined): StudioPath {
  if (section === null) return SIMPLE;
  if (section === SETTINGS_PLACE) return SETTINGS;
  const items: readonly StudioNavItem[] = STUDIO_NAV.flatMap(
    (group): readonly StudioNavItem[] => group.items,
  );
  const target = items.find((item) => item.label === section)?.target;
  return target?.scope === "page" ? target.to : PRO_HOME;
}
